<script setup>
import { reactive } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  type_partie_estimations: { type: Array,  default: () => [] },
  labels:                  { type: Object, default: () => ({}) },
  save_url:                { type: String, default: '' },
  back_url:                { type: String, default: '/administration' },
})

const { success, error: toastError } = useToast()

// Shape attendu par le controller : params[:type_partie_estimations][id][field]
// Reconstruction d'un objet keyed par id pour matcher config_tpe_params / format_update
const initialPayload = {}
props.type_partie_estimations.forEach(t => {
  initialPayload[String(t.id)] = {
    code:                    t.code ?? '',
    description:             t.description ?? '',
    ordre:                   t.ordre ?? '',
    import_file_column_name: t.import_file_column_name ?? '',
    dependent:               t.dependent ?? '',
    suivre:                  !!t.suivre,
  }
})

const form = useForm({
  type_partie_estimations: initialPayload,
})

function submit() {
  form.post(props.save_url, {
    preserveScroll: true,
    onError: (errs) => {
      const list = Object.values(errs).flat()
      if (list.length) list.forEach(m => toastError(m))
      else toastError('Erreur lors de la mise à jour.')
    },
  })
}

function cancel() {
  window.location.href = props.back_url
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Configuration &rsaquo; Bordereau
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Configuration du bordereau
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ type_partie_estimations.length }} type{{ type_partie_estimations.length > 1 ? 's' : '' }} de partie
            </p>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Sous-navigation -->
        <nav class="flex items-center gap-1 bg-surface-container-low rounded-md p-1 border border-outline-variant/10 mb-6">
          <a href="/administration" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Liste
          </a>
          <a href="/administration/edit_config_client" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Client
          </a>
          <a href="/administration/edit_logos" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Logos
          </a>
          <a href="/administration/edit_config_bordereau" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-primary bg-surface-container rounded-sm border-l-4 border-tertiary">
            Bordereau
          </a>
          <a href="/administration/edit_config_importation" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Importation
          </a>
          <a href="/administration/edit_config_module" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Modules
          </a>
        </nav>

        <!-- Form Card -->
        <form @submit.prevent="submit" class="space-y-4">
          <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-outline-variant/10">
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.code || 'Catégorie' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.description || 'Description' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">{{ labels.ordre || 'Ordre' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.import_file_column_name || "Colonne d'importation" }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.dependant || 'Dépendance' }}</th>
                    <th class="text-center px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24">{{ labels.suivre || 'Suivre' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="type_partie_estimations.length === 0">
                    <td colspan="6" class="px-6 py-6 text-center text-sm text-outline/60">
                      Aucune configuration de bordereau.
                    </td>
                  </tr>
                  <tr
                    v-for="tpe in type_partie_estimations"
                    :key="tpe.id"
                    class="border-b border-outline-variant/5 hover:bg-surface-container-high/20 transition-colors"
                  >
                    <td class="px-4 py-2">
                      <input
                        v-model="form.type_partie_estimations[String(tpe.id)].code"
                        type="text"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm font-bold focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                      />
                    </td>
                    <td class="px-4 py-2">
                      <textarea
                        v-model="form.type_partie_estimations[String(tpe.id)].description"
                        rows="1"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-y min-h-[32px]"
                      ></textarea>
                    </td>
                    <td class="px-4 py-2 w-20">
                      <input
                        v-model.number="form.type_partie_estimations[String(tpe.id)].ordre"
                        type="number"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                      />
                    </td>
                    <td class="px-4 py-2">
                      <input
                        v-model="form.type_partie_estimations[String(tpe.id)].import_file_column_name"
                        type="text"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                      />
                    </td>
                    <td class="px-4 py-2">
                      <input
                        v-model="form.type_partie_estimations[String(tpe.id)].dependent"
                        type="text"
                        placeholder="—"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                      />
                    </td>
                    <td class="px-4 py-2 w-24 text-center">
                      <label class="relative inline-flex items-center cursor-pointer">
                        <input
                          v-model="form.type_partie_estimations[String(tpe.id)].suivre"
                          type="checkbox"
                          class="sr-only peer"
                        />
                        <div class="w-10 h-5 bg-surface-container-highest rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-on-surface after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-5 peer-checked:after:bg-on-primary"></div>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              @click="cancel"
              class="px-6 py-3 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="form.processing"
              class="px-8 py-3 machined-sheen text-on-primary font-black rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
            >
              <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
              <span v-else class="material-symbols-outlined text-base">save</span>
              Enregistrer
            </button>
          </div>
        </form>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
